// A job's value, variations and invoices (round 45, server routes/jobMoney.ts).
//
// Every call answers with the job's whole money block, so the Job value,
// Variations and Invoicing tabs and the cashflow chart redraw from one read.
// Figures are integer cents, GST-inclusive; amounts are SENT as the text a
// person typed ("1250.50") and the server parses them, as quote rates are.
import { postForm, request } from './api';
import type { Attachment } from './documentsApi';

export type VariationStatus = 'pending' | 'approved' | 'rejected';

export interface JobMoneyFigures {
  originalValueCents: number;
  approvedVariationsCents: number;
  currentValueCents: number;
  invoicedCents: number;
  outstandingCents: number;
}

export interface Variation {
  id: string;
  description: string;
  valueCents: number;
  status: VariationStatus;
  requestedDate: string;
  approvedDate: string | null;
  createdByName: string;
  createdAt: string;
}

export interface JobInvoice {
  id: string;
  invoiceNumber: string;
  invoicedDate: string;
  amountCents: number | null;
  createdByName: string;
  createdAt: string;
  file: Attachment | null;
}

export interface JobMoneyBlock {
  money: JobMoneyFigures;
  variations: Variation[];
  invoices: JobInvoice[];
}

function invoiceForm(fields: { invoiceNumber?: string; invoicedDate?: string; amount?: string }, file: File | null) {
  const form = new FormData();
  if (fields.invoiceNumber !== undefined) form.append('invoiceNumber', fields.invoiceNumber);
  if (fields.invoicedDate !== undefined && fields.invoicedDate !== '') form.append('invoicedDate', fields.invoicedDate);
  if (fields.amount !== undefined) form.append('amount', fields.amount);
  if (file) form.append('file', file, file.name);
  return form;
}

export const jobMoneyApi = {
  setOriginalValue: (jobId: string, originalValue: string) =>
    request<JobMoneyBlock>(`/jobs/${jobId}/value`, { method: 'PUT', body: { originalValue } }),

  addVariation: (jobId: string, body: { description: string; value: string; requestedDate?: string }) =>
    request<JobMoneyBlock>(`/jobs/${jobId}/variations`, { method: 'POST', body }),
  updateVariation: (
    jobId: string,
    variationId: string,
    body: { description?: string; value?: string; requestedDate?: string; status?: VariationStatus }
  ) => request<JobMoneyBlock>(`/jobs/${jobId}/variations/${variationId}`, { method: 'PATCH', body }),
  deleteVariation: (jobId: string, variationId: string) =>
    request<JobMoneyBlock>(`/jobs/${jobId}/variations/${variationId}`, { method: 'DELETE' }),

  addInvoice: (
    jobId: string,
    fields: { invoiceNumber: string; invoicedDate?: string; amount: string },
    file: File | null
  ) => postForm<JobMoneyBlock>(`/jobs/${jobId}/invoices`, invoiceForm(fields, file)),
  updateInvoice: (
    jobId: string,
    invoiceId: string,
    fields: { invoiceNumber?: string; invoicedDate?: string; amount?: string },
    file: File | null
  ) => postForm<JobMoneyBlock>(`/jobs/${jobId}/invoices/${invoiceId}`, invoiceForm(fields, file), undefined, 'PATCH'),
  deleteInvoice: (jobId: string, invoiceId: string) =>
    request<JobMoneyBlock>(`/jobs/${jobId}/invoices/${invoiceId}`, { method: 'DELETE' }),
};
